
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Eye, Trash2, Search, RefreshCw } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import ViewProjectDialog from './dialogs/ViewProjectDialog';

const ProjectsTab = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          profiles:user_id (full_name, email),
          projects_feedbacks:projects_feedbacks (
            feedback_id
          )
        `)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      console.log('Fetched projects:', data);
      
      // Count responses for each project
      const projectsWithCounts = await Promise.all((data || []).map(async (project) => {
        // Calculate feedback counts
        const feedbackIds = project.projects_feedbacks?.map((pf: any) => pf.feedback_id) || [];
        
        let responseCount = 0;
        if (feedbackIds.length > 0) {
          // Count completed feedbacks
          const { count, error: countError } = await supabase
            .from('feedbacks')
            .select('*', { count: 'exact' })
            .in('id', feedbackIds);
          
          if (!countError) {
            responseCount = count || 0;
          }
        }
        
        return {
          ...project,
          feedbackCount: project.projects_feedbacks?.length || 0,
          responseCount
        };
      }));
      
      setProjects(projectsWithCounts);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les projets.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewProject = (project: any) => {
    setSelectedProject(project);
    setIsViewDialogOpen(true);
  };

  const handleDeleteProject = async (projectId: string) => {
    setIsDeleting(true);
    try {
      // First, delete related records in junction tables
      await supabase.from('projects_feedbacks').delete().eq('project_id', projectId);
      await supabase.from('projects_requests').delete().eq('project_id', projectId);
      
      // Then delete the project itself
      const { error } = await supabase.from('projects').delete().eq('id', projectId);
      
      if (error) throw error;
      
      toast({
        title: "Projet supprimé",
        description: "Le projet a été supprimé avec succès."
      });
      
      // Refresh projects list
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le projet.",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getFilteredProjects = () => {
    return projects.filter(project => {
      // Apply search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === '' || 
        project.name.toLowerCase().includes(searchLower) ||
        (project.profiles?.email && project.profiles.email.toLowerCase().includes(searchLower)) ||
        (project.profiles?.full_name && project.profiles.full_name.toLowerCase().includes(searchLower));
      
      return matchesSearch;
    });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div>
          <CardTitle>Gestion des projets</CardTitle>
          <CardDescription>Visualisez et gérez tous les projets créés par les utilisateurs</CardDescription>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchProjects}
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Rafraîchir
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-center mb-4">
          <Search className="h-4 w-4 text-gray-400 mr-2" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brandBlue"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Propriétaire</TableHead>
                  <TableHead>Contexte</TableHead>
                  <TableHead className="text-center">Nb feedbacks</TableHead>
                  <TableHead className="text-center">Réponses reçues</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredProjects().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      Aucun projet trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  getFilteredProjects().map((project) => (
                    <TableRow key={project.id}>
                      <TableCell className="font-medium">{project.name}</TableCell>
                      <TableCell>{project.profiles?.email || "N/A"}</TableCell>
                      <TableCell>{project.category || "N/A"}</TableCell>
                      <TableCell className="text-center">{project.feedbackCount}</TableCell>
                      <TableCell className="text-center">{project.responseCount}</TableCell>
                      <TableCell>{new Date(project.created_at).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewProject(project)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Voir
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Supprimer
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Êtes-vous sûr de vouloir supprimer le projet "{project.name}" ? Cette action est irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteProject(project.id)}
                                  disabled={isDeleting}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  {isDeleting ? 'Suppression...' : 'Supprimer'}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
        
        {selectedProject && (
          <ViewProjectDialog
            isOpen={isViewDialogOpen}
            onClose={() => setIsViewDialogOpen(false)}
            project={selectedProject}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ProjectsTab;
